import { useMemo } from 'react'
import { ShaderParamControls } from '@/components/canvas/node-settings/shader/ShaderParamControls'
import { ShaderPromptHistory } from '@/components/canvas/shader/ShaderPromptHistory'
import { mergeParamLabels, parseParams } from '@/lib/canvas/shader/params'
import type { ShaderPromptEntry } from '@/lib/canvas/shader/authoring/types'
import type { ExtractedParam } from '@/lib/canvas/shader/types'

interface ShaderNodeAuthoringProps {
  hasCode: boolean
  code: string
  extractedParams?: ExtractedParam[]
  paramValues: Record<string, unknown>
  history: readonly ShaderPromptEntry[]
  onParamChange: (name: string, value: number | string | boolean) => void
  onReusePrompt: (prompt: string) => void
  onRestore: (entry: ShaderPromptEntry) => void
}

/**
 * The authoring surface on a Blank Shader: what the node is for, the parameters
 * its code declares, and the prompts submitted on this node alone.
 */
export function ShaderNodeAuthoring({
  hasCode,
  code,
  extractedParams,
  paramValues,
  history,
  onParamChange,
  onReusePrompt,
  onRestore,
}: ShaderNodeAuthoringProps) {
  const params = useMemo(() => mergeParamLabels(parseParams(code), extractedParams), [code, extractedParams])

  return (
    <div className="flex flex-col gap-2">
      {!hasCode && (
        <p className="px-0.5 text-[11px] leading-snug text-muted-foreground">
          Write a live visual from a prompt or from GLSL. Image, video, audio, and text inputs connect on the left.
        </p>
      )}
      {params.length > 0 && (
        <div role="group" aria-label="Shader parameters" className="nodrag nowheel nopan rounded-md border border-border">
          <ShaderParamControls params={params} values={paramValues} onChange={onParamChange} embedded />
        </div>
      )}
      {history.length > 0 && (
        <ShaderPromptHistory entries={history} onReuse={onReusePrompt} onRestore={onRestore} currentCode={code} />
      )}
    </div>
  )
}
